import { Component, ViewChild, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { filter } from 'rxjs';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { NotificationService } from '@progress/kendo-angular-notification';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { LabelModule } from '@progress/kendo-angular-label';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { DatatableComponent } from '../../../../../../../core/components/datatable/datatable.component';
import { BaseComponent } from '../../../../../../../core/shared/common/base.component';
import { ServerException } from '../../../../../../../core/models/entities';
import { environment } from '../../../../../../../../environments/environment';
import { ServerExceptionForm, ServerExceptionFormService } from './service/server-form.service';
import { ServerExceptionRequest, ServerExceptionService } from './service/server.service';
import DataTable from 'datatables.net-dt';

@Component({
  selector: 'app-server',
  standalone: true,
  imports: [
    CommonModule,
    LayoutModule,
    InputsModule,
    LabelModule,
    ButtonsModule,
    DialogModule,
    DropDownsModule,
    ReactiveFormsModule,
    DatatableComponent,
  ],
  templateUrl: './server.component.html',
  styleUrl: './server.component.scss',
  // ServerExceptionService is the only one of these grid services declared
  // without providedIn: 'root', so it has to be provided here.
  providers: [ServerExceptionService],
})
export class ServerComponent extends BaseComponent {
  @ViewChild(DatatableComponent) private table?: DatatableComponent;

  public gridService = inject(ServerExceptionService);
  public route = inject(ActivatedRoute);
  private formService = inject(ServerExceptionFormService);
  private notificationService = inject(NotificationService);

  private baseUrl = `${environment.apiUrl}/v1/organization/administrative/serverexceptions`;
  public id!: number;

  public datatableUrl = signal<string>('');
  public formGroup = signal<FormGroup<ServerExceptionForm> | undefined>(undefined);
  public isNew = signal<boolean>(false);
  public isSaving = signal<boolean>(false);
  private rows = new Map<string, ServerException>();

  public listData = [
    { text: 'Backup Server', value: 'backup' },
    { text: 'Vulnerability Scanner', value: 'vulnerability scanner' },
    { text: 'Spam and Email', value: 'spam and email' },
    { text: 'DNS Server', value: 'DNS' },
    { text: 'DHCP Server', value: 'DHCP' },
    { text: 'Public Web Server', value: 'Public Web' },
    { text: 'RDP Server', value: 'RDP' },
    { text: 'Other - Public', value: 'Other publicly accessible servers & services' },
    { text: 'Other - Crictical', value: 'Other critical server' },
  ];

  public datatableColumns = [
    { data: 'server_name', name: 'server_name', title: 'Host Name', type: 'string', render: DataTable.render.text() },
    { data: 'server_type', name: 'server_type', title: 'Server Type', type: 'string', render: DataTable.render.text() },
    { data: 'ip_address', name: 'ip_address', title: 'IP Address', type: 'string', render: DataTable.render.text() },
    {
      data: 'notes_for_analyst',
      name: 'notes_for_analyst',
      title: 'Analyst Notes',
      type: 'string',
      render: DataTable.render.text(),
    },
    {
      data: null,
      name: 'actions',
      title: 'Action',
      type: 'actions',
      orderable: false,
      render: (_data: unknown, _type: unknown, row: ServerException) => {
        this.rows.set(String(row.pk), row);
        return `
          <div class="d-flex gap-2">
            <button type="button" class="cyflare-btn-secondary cyflare-btn-sm action-edit" data-pk="${row.pk}">
              Edit
            </button>
            <button type="button" class="cyflare-btn-danger cyflare-btn-sm action-remove" data-pk="${row.pk}">
              Remove
            </button>
          </div>
        `;
      },
    },
  ];

  constructor() {
    super();
    this.initEffect();
  }

  private initEffect() {
    effect(
      () => {
        this.route.queryParams.pipe(filter((params) => params['organizationId'])).subscribe((data) => {
          this.id = parseInt(data['organizationId'], 10);
          // Still the source of truth for update and delete, which build their
          // endpoint from it, even though the grid no longer reads through the
          // service.
          this.gridService.url = `${this.baseUrl}(${this.id})/`;
          this.rows.clear();
          this.datatableUrl.set(this.gridService.url);
        });
      },
      { allowSignalWrites: true }
    );
  }

  public onTableClick(event: MouseEvent): void {
    const button = (event.target as HTMLElement).closest('button');
    const pk = button?.getAttribute('data-pk');
    if (!button || !pk) {
      return;
    }

    if (button.classList.contains('action-edit')) {
      this.openEditor(this.rows.get(pk));
    } else if (button.classList.contains('action-remove')) {
      this.remove(pk);
    }
  }

  public openEditor(serverException?: ServerException): void {
    this.isNew.set(!serverException);
    this.formGroup.set(this.formService.createServerExceptionForm(serverException));
  }

  public closeEditor(): void {
    // The dialog close button and the backdrop both land here; ignore them
    // while a save is in flight so the user cannot walk away mid-request.
    if (this.isSaving()) {
      return;
    }
    this.formGroup.set(undefined);
  }

  public save(): void {
    const formGroup = this.formGroup();
    if (!formGroup || this.isSaving()) {
      return;
    }
    const formValue = formGroup.value;
    const payload: ServerExceptionRequest = {
      organization_id: this.id,
      servers_exceptions: {
        pk: this.isNew() ? null : formValue?.pk!,
        ip_address: formValue?.ip_address!,
        server_name: formValue?.server_name!,
        notes_for_analyst: formValue?.notes_for_analyst!,
        server_type: formValue?.server_type!,
      },
    };

    const request = this.isNew()
      ? this.gridService.createServerException(payload)
      : this.gridService.updateServerException(payload.servers_exceptions);

    this.isSaving.set(true);
    request.subscribe({
      next: () => {
        this.isSaving.set(false);
        this.closeEditor();
        this.table?.reload();
      },
      error: (error) => {
        this.isSaving.set(false);
        console.error(error);
        this.showError('Failed to save the server exception.');
      },
    });
  }

  private remove(pk: string): void {
    this.gridService.deleteServerException(Number(pk)).subscribe({
      next: () => {
        this.table?.reload();
      },
      error: (error) => {
        console.error(error);
        this.showError('Failed to delete the server exception.');
      },
    });
  }

  private showError(message: string): void {
    this.notificationService.show({
      content: message,
      cssClass: 'notification-error',
      animation: { type: 'fade', duration: 400 },
      position: { horizontal: 'right', vertical: 'top' },
      type: { style: 'error', icon: true },
      closable: true,
      hideAfter: 5000,
    });
  }
}
